import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import * as DV from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Divider } from "@impero-com/ui";

import { MetadataInstanceValue, EntityView } from "api/generated/model";
import { StringInput } from "components/form/legacyFields/stringInput";
import { TextAreaInput } from "components/form/legacyFields/textAreaInput";
import { MetadataFieldSectionsLayout } from "org/risks/content/sections";
import * as R from "org/risks/types";
import { UpdateRootNodeLinkedResourcesCallback } from "org/risks/widgets/attachedFilesField";
import { NodeScope } from "org/risks/widgets/nodeScope";

import * as T from "../types";
import * as U from "../utils";

class NodeDetailsTab extends React.PureComponent<NodeDetailsTabProps> {
    render = () => {
        const {
            riskDirectoryView,
            nodeDescriptor,
            metadataExtractors,
            canEditNode,
            entities,
        } = this.props;
        const node = nodeDescriptor.value;
        const nodeScope = U.getScope(node);

        if (node.type === "RiskDirectory") {
            throw new Error("Unexpected node type " + node.type);
        }

        const { sections, matchCount: nbSections } =
            U.filterAndResolveDirectorySections(
                riskDirectoryView,
                metadataExtractors,
                node,
                { riskDirectoryLevel: true, entityLevel: false }
            );
        return (
            <Form>
                {canEditNode ? (
                    <>
                        <StringInput
                            id="node-name"
                            type="EDIT"
                            label={t("RiskDirectory_NodeUpdate_Field_Name")}
                            value={node.content.name}
                            onChange={(name) =>
                                this.props.onQueueAsyncNodeUpdate(
                                    Promise.resolve((node) => {
                                        if (name !== node.content.name) {
                                            return { name };
                                        } else {
                                            return undefined;
                                        }
                                    })
                                )
                            }
                            validators={[
                                {
                                    kind: "ruleset",
                                    required: true,
                                    whitespace: true,
                                },
                                {
                                    kind: "custom",
                                    validator: (
                                        nodeName: string | undefined
                                    ) => {
                                        if (nodeName === undefined) {
                                            // Should be ruled-out by required: true
                                            return undefined;
                                        }
                                        const parentNode: T.DirectoryNode =
                                            nodeDescriptor.ancestors[
                                                nodeDescriptor.ancestors
                                                    .length - 1
                                            ];
                                        switch (parentNode.type) {
                                            case "RiskDirectory":
                                            case "Process":
                                                return U.validateNodeName(
                                                    nodeName,
                                                    node.id,
                                                    node.type,
                                                    nodeScope,
                                                    parentNode
                                                );
                                            case "Risk":
                                                throw new Error(
                                                    "Can't add a child node in a risk"
                                                );
                                            default:
                                                assertNever(parentNode);
                                        }
                                    },
                                },
                            ]}
                        />
                        <TextAreaInput
                            id="node-description"
                            type="EDIT"
                            label={t(
                                "RiskDirectory_NodeUpdate_Field_Description"
                            )}
                            value={node.content.description}
                            onChange={(description) =>
                                this.props.onQueueAsyncNodeUpdate(
                                    Promise.resolve((node) => {
                                        if (
                                            description !==
                                            node.content.description
                                        ) {
                                            return { description };
                                        } else {
                                            return undefined;
                                        }
                                    })
                                )
                            }
                        />
                        <NodeScope
                            type="READ_ONLY"
                            entities={entities}
                            nodeType={node.type}
                            {...nodeScope}
                        />
                        {nbSections > 0 ? (
                            <>
                                <Divider />
                                <MetadataFieldSectionsLayout
                                    type="EDIT"
                                    sections={sections}
                                    node={node}
                                    extractors={metadataExtractors}
                                    onChangeFieldValueAsync={async (
                                        fieldId,
                                        promiseValue,
                                        callback
                                    ) => {
                                        const promiseAction = async () => {
                                            const value = await promiseValue;
                                            const action: T.NodeUpdateAction<
                                                T.DirectoryNode
                                            > = (node: T.DirectoryNode) => {
                                                const oldValue:
                                                    | MetadataInstanceValue
                                                    | undefined =
                                                    node.content.metadataValues[
                                                        fieldId
                                                    ];
                                                const newMetadataValue =
                                                    callback(oldValue, value);
                                                return {
                                                    metadataValues: {
                                                        [fieldId]:
                                                            newMetadataValue,
                                                    },
                                                };
                                            };
                                            return action;
                                        };
                                        return this.props.onQueueAsyncNodeUpdate(
                                            promiseAction()
                                        );
                                    }}
                                    updateRootNodeLinkedResources={
                                        this.props.updateRootNodeLinkedResources
                                    }
                                />
                            </>
                        ) : null}
                    </>
                ) : (
                    <>
                        <StringInput
                            type="READ_ONLY"
                            label={t("RiskDirectory_NodeUpdate_Field_Name")}
                            value={node.content.name}
                        />
                        <TextAreaInput
                            type="READ_ONLY"
                            label={t(
                                "RiskDirectory_NodeUpdate_Field_Description"
                            )}
                            value={node.content.description}
                        />
                        <NodeScope
                            type="READ_ONLY"
                            entities={entities}
                            nodeType={node.type}
                            {...nodeScope}
                        />
                        {nbSections > 0 ? (
                            <>
                                <Divider />
                                <MetadataFieldSectionsLayout
                                    type="READ_ONLY"
                                    sections={sections}
                                    node={node}
                                    extractors={metadataExtractors}
                                    updateRootNodeLinkedResources={
                                        this.props.updateRootNodeLinkedResources
                                    }
                                />
                            </>
                        ) : null}
                    </>
                )}
            </Form>
        );
    };
}

interface NodeDetailsTabProps {
    riskDirectoryView: R.RiskDirectoryView;
    nodeDescriptor: DV.NodeDescriptor<T.DirectoryNode>;
    entities: Record<number, EntityView>;
    metadataExtractors: Array<
        DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>
    >;
    canEditNode: boolean;
    onQueueAsyncNodeUpdate: (
        promiseAction: Promise<T.NodeUpdateAction<T.DirectoryNode>>
    ) => Promise<void>;
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
}

export { NodeDetailsTab, type NodeDetailsTabProps };
